import {
  Button,
  CircularProgress,
  CssBaseline,
  Grid,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import queryString from 'query-string';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { GoogleSignInButton, Message } from '../components';
import { serverErrorsl10n } from '../l10n/common';
import * as api from '../services/api';
import * as authActions from '../store/actions/authActions';
import { config, track } from '../utils';

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3, 2),
  },
  logo: {
    width: 64,
    height: 'auto',
    marginBottom: theme.spacing(3),
  },
  card: {
    width: '100%',
    maxWidth: 480,
  },
  copy: {
    margin: theme.spacing(2, 0, 3),
  },
  button: {
    marginTop: theme.spacing(2),
  },
  divider: {
    margin: theme.spacing(3, 0),
    textAlign: 'center',
  },
  form: {
    marginTop: theme.spacing(2),
  },
  progress: {
    display: 'block',
    margin: theme.spacing(3, 'auto'),
  },
}));

const FacebookLoginMigrationView = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { flow, status, step } = queryString.parse(location.search);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [passwordValues, setPasswordValues] = useState({
    email: '',
    password: '',
    passwordConfirm: '',
  });
  const storedGoogleIdToken = sessionStorage.getItem(
    authActions.FACEBOOK_MIGRATION_GOOGLE_TOKEN_KEY
  );

  const language = (i18n.resolvedLanguage || i18n.language || 'en').split(
    '-'
  )[0];
  const migrationErrorMessage = useCallback(
    (error, fallbackKey) =>
      (serverErrorsl10n[language] || serverErrorsl10n.en)[error?.message] ||
      t(fallbackKey),
    [language, t]
  );

  useEffect(() => {
    track.screen('FacebookLoginMigrationView');
  }, []);

  useEffect(() => {
    if (status !== 'success') {
      return;
    }

    if (storedGoogleIdToken) {
      dispatch(authActions.googleAuth(storedGoogleIdToken));
    }
  }, [dispatch, status, storedGoogleIdToken]);

  const beginFacebookVerification = async () => {
    setIsLoading(true);
    setErrorMessage('');

    try {
      let activeFlow = flow;

      if (!activeFlow) {
        const response = await api.authService.startFacebookMigration({
          ...(storedGoogleIdToken && { idToken: storedGoogleIdToken }),
        });
        activeFlow = response.flow;
      }

      window.location.assign(
        `${config.apiHost}/auth/facebook/migration?flow=${encodeURIComponent(
          activeFlow
        )}`
      );
    } catch (error) {
      setErrorMessage(
        migrationErrorMessage(error, 'facebookMigration.startError')
      );
      setIsLoading(false);
    }
  };

  const cancelMigration = () => {
    sessionStorage.removeItem(authActions.FACEBOOK_MIGRATION_GOOGLE_TOKEN_KEY);

    if (flow) {
      window.location.assign(
        `${
          config.apiHost
        }/auth/facebook/migration/cancel?flow=${encodeURIComponent(flow)}`
      );
      return;
    }

    history.replace('/');
  };

  const returnToAccessNow = () => {
    if (flow) {
      window.location.assign(
        `${
          config.apiHost
        }/auth/facebook/migration/return?flow=${encodeURIComponent(flow)}`
      );
      return;
    }

    history.replace('/');
  };

  const completeLogin = useCallback(
    (response, method) => {
      const { migrationRedirectUrl, ...user } = response;

      track.setUser(user.email ? { email: user.email } : {});
      track.setTag('login', method);
      track.analytics({
        category: 'login',
        data: { email: user.email, method },
      });

      dispatch({
        type: 'FACEBOOK_MIGRATION_SUCCESS',
        payload: {
          isLoading: false,
          isAuthenticated: true,
          isFacebookMigrationRequired: false,
          thirdPartyAuth: method === 'google' ? '1' : '0',
          errorMessage: '',
          ...user,
        },
      });

      sessionStorage.removeItem(
        authActions.FACEBOOK_MIGRATION_GOOGLE_TOKEN_KEY
      );

      if (migrationRedirectUrl?.startsWith('accessnow')) {
        window.location.assign(migrationRedirectUrl);
      } else {
        history.replace('/');
      }
    },
    [dispatch, history]
  );

  const completeWithGoogle = useCallback(
    async (idToken) => {
      setIsLoading(true);
      setErrorMessage('');

      try {
        const response =
          await api.authService.completeFacebookMigrationWithGoogle(
            flow,
            idToken
          );
        completeLogin(response, 'google');
      } catch (error) {
        setErrorMessage(
          migrationErrorMessage(error, 'facebookMigration.googleLinkError')
        );
        setIsLoading(false);
      }
    },
    [completeLogin, flow, migrationErrorMessage]
  );

  const completeWithPassword = async (event) => {
    event.preventDefault();
    setErrorMessage('');

    if (passwordValues.password !== passwordValues.passwordConfirm) {
      setErrorMessage(t('facebookMigration.passwordMismatch'));
      return;
    }

    setIsLoading(true);

    try {
      const response =
        await api.authService.completeFacebookMigrationWithPassword({
          flow,
          ...passwordValues,
        });
      completeLogin(response, 'password');
    } catch (error) {
      setErrorMessage(
        migrationErrorMessage(error, 'facebookMigration.passwordSetError')
      );
      setIsLoading(false);
    }
  };

  const handlePasswordChange = (event) => {
    const { name, value } = event.target;
    setPasswordValues((current) => ({ ...current, [name]: value }));
  };

  const content = () => {
    if (status === 'already-migrated') {
      return (
        <>
          <Typography variant="h2">
            {t('facebookMigration.alreadyMigratedTitle')}
          </Typography>
          <Typography className={classes.copy}>
            {t('facebookMigration.alreadyMigratedBody')}
          </Typography>
          <Button
            color="primary"
            variant="contained"
            size="large"
            disabled={isLoading}
            onClick={cancelMigration}
            fullWidth
          >
            {t('facebookMigration.returnToAccessNow')}
          </Button>
        </>
      );
    }

    if (status === 'error') {
      return (
        <>
          <Typography variant="h2">
            {t('facebookMigration.verificationErrorTitle')}
          </Typography>
          <Typography className={classes.copy}>
            {t('facebookMigration.verificationErrorBody')}
          </Typography>
          <Button
            color="primary"
            variant="contained"
            size="large"
            disabled={isLoading}
            onClick={beginFacebookVerification}
            fullWidth
          >
            {t('facebookMigration.tryAgain')}
          </Button>
          <Button
            className={classes.button}
            color="primary"
            variant="outlined"
            size="large"
            disabled={isLoading}
            onClick={cancelMigration}
            fullWidth
          >
            {t('messages.cancel')}
          </Button>
        </>
      );
    }

    if (status === 'success') {
      return (
        <>
          <Typography variant="h2">
            {t('facebookMigration.completeTitle')}
          </Typography>
          <Typography className={classes.copy}>
            {t('facebookMigration.completeBody')}
          </Typography>
          {storedGoogleIdToken ? (
            <CircularProgress className={classes.progress} />
          ) : (
            <Button
              color="primary"
              variant="contained"
              size="large"
              onClick={returnToAccessNow}
              fullWidth
            >
              {t('facebookMigration.returnToAccessNow')}
            </Button>
          )}
        </>
      );
    }

    if (step === 'link' && flow) {
      return (
        <>
          <Typography variant="h2">
            {t('facebookMigration.chooseMethodTitle')}
          </Typography>
          <Typography className={classes.copy}>
            {t('facebookMigration.chooseMethodBody')}
          </Typography>
          <GoogleSignInButton
            disabled={isLoading}
            onCredential={completeWithGoogle}
          />
          <Typography className={classes.divider}>
            {t('facebookMigration.orSetPassword')}
          </Typography>
          <form className={classes.form} onSubmit={completeWithPassword}>
            <TextField
              name="email"
              type="email"
              label={t('facebookMigration.email')}
              value={passwordValues.email}
              onChange={handlePasswordChange}
              disabled={isLoading}
              required
              fullWidth
            />
            <TextField
              name="password"
              type="password"
              label={t('facebookMigration.newPassword')}
              helperText={t('facebookMigration.passwordHint')}
              value={passwordValues.password}
              onChange={handlePasswordChange}
              disabled={isLoading}
              required
              fullWidth
            />
            <TextField
              name="passwordConfirm"
              type="password"
              label={t('facebookMigration.confirmPassword')}
              value={passwordValues.passwordConfirm}
              onChange={handlePasswordChange}
              disabled={isLoading}
              required
              fullWidth
            />
            <Button
              className={classes.button}
              type="submit"
              color="primary"
              variant="contained"
              size="large"
              disabled={isLoading}
              fullWidth
            >
              {t('facebookMigration.finishWithPassword')}
            </Button>
          </form>
        </>
      );
    }

    return (
      <>
        <Typography variant="h2">
          {t('facebookMigration.introTitle')}
        </Typography>
        <Typography className={classes.copy}>
          {t('facebookMigration.introBody')}
        </Typography>
        <Button
          color="primary"
          variant="contained"
          size="large"
          disabled={isLoading}
          onClick={beginFacebookVerification}
          fullWidth
        >
          {t('facebookMigration.verifyWithFacebook')}
        </Button>
      </>
    );
  };

  return (
    <main className={classes.root}>
      <CssBaseline />
      <Grid className={classes.card} container direction="column">
        <img
          className={classes.logo}
          src="/images/logo/an-dark-logo.svg"
          alt="AccessNow"
        />
        {errorMessage ? (
          <Message variant="error" message={errorMessage} role="alert" />
        ) : null}
        {content()}
      </Grid>
    </main>
  );
};

export default FacebookLoginMigrationView;
